import { type ColumnDef } from "@tanstack/react-table";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageContainer, PageHeader } from "@/components/layout";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { Permissions } from "../../entities/permissions";
import { hasPermission } from "../login/selectors";
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useEditTagMutation,
  useTagListQuery,
} from "../../services/api";

interface Tag {
  id: number;
  name: string;
  color: string | null;
  active: boolean;
}

interface TagsResponse {
  items?: Tag[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
}

const DEFAULT_COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#10B981",
  "#14B8A6",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#EC4899",
  "#64748B",
];

function isValidHex(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

export default function Tags() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const hasAddPermission = useSelector(hasPermission(Permissions.PAGE_TAGS_ADD));
  const hasEditPermission = useSelector(hasPermission(Permissions.PAGE_TAGS_EDIT));
  const hasRemovePermission = useSelector(hasPermission(Permissions.PAGE_TAGS_REMOVE));

  const [page, setPage] = useState(0);
  const [includeInactive, setIncludeInactive] = useState(false);

  const { data: tags, isFetching: fetchingList } = useTagListQuery({
    range: { page: page + 1 },
    filters: { includeInactive },
  }) as { data?: TagsResponse; isFetching: boolean };

  const [createTag, { isLoading: isCreating }] = useCreateTagMutation();
  const [editTag, { isLoading: isEditing }] = useEditTagMutation();
  const [deleteTag, { isLoading: isDeleting }] = useDeleteTagMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Tag | null>(null);

  const rows = tags?.items ?? [];

  // No per-row Edit / Delete columns — row click opens the edit dialog,
  // which now carries the destructive Delete button in its footer (when
  // the user has remove permission and the tag is still active).
  const columns = useMemo<ColumnDef<Tag>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("tags.columns.name"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span
              className="inline-block size-3 rounded-full border border-border"
              style={{ backgroundColor: row.original.color ?? "transparent" }}
              aria-hidden
            />
            <span>{row.original.name}</span>
            {!row.original.active ? (
              <Badge variant="outline" className="ml-2">
                {t("tags.inactive")}
              </Badge>
            ) : null}
          </div>
        ),
      },
    ],
    [t]
  );

  function onRowClick(row: Tag) {
    if (!hasEditPermission) return;
    setSelected(row);
    setEditOpen(true);
  }

  const pageSize = tags?.range?.limit ?? 15;
  const pageCount = tags?.itemCount ? Math.max(1, Math.ceil(tags.itemCount / pageSize)) : 1;

  async function handleCreate(name: string, color: string | null) {
    try {
      await createTag({ name, color }).unwrap();
      showNotification("success", t("tags.create.success", { name }));
      setAddOpen(false);
    } catch (err) {
      const e = err as { status?: number };
      if (e?.status === 409) {
        showNotification("error", t("tags.errors.name-already-used"));
      } else {
        showNotification("error", t("common.api-error"));
      }
    }
  }

  async function handleEdit(id: number, patch: Partial<Tag>) {
    try {
      await editTag({ id, ...patch }).unwrap();
      showNotification("success", t("tags.edit.success"));
      setEditOpen(false);
    } catch (err) {
      const e = err as { status?: number };
      if (e?.status === 409) {
        showNotification("error", t("tags.errors.name-already-used"));
      } else {
        showNotification("error", t("common.api-error"));
      }
    }
  }

  async function handleDelete() {
    if (!selected) return;
    try {
      await deleteTag(selected.id).unwrap();
      showNotification("success", t("tags.delete.success", { name: selected.name }));
      setDeleteOpen(false);
      // Tear down the edit dialog too — the underlying tag is gone.
      setEditOpen(false);
    } catch {
      showNotification("error", t("common.api-error"));
    }
  }

  return (
    <>
      <PageContainer title={t("tags.tab-title")}>
        <PageHeader
          title={t("tags.page-title")}
          description={t("tags.info-message")}
          actions={
            hasAddPermission ? (
              <Button onClick={() => setAddOpen(true)}>
                <PlusIcon className="size-4" />
                {t("tags.add-tag")}
              </Button>
            ) : null
          }
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <Checkbox
            id="tags-include-inactive"
            checked={includeInactive}
            onCheckedChange={(v) => setIncludeInactive(Boolean(v))}
          />
          <Label htmlFor="tags-include-inactive" className="text-sm">
            {t("tags.show-inactive")}
          </Label>
        </div>

        <div className="mt-6">
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={t("common.no-data", "No data")}
            onRowClick={hasEditPermission ? onRowClick : undefined}
            loading={fetchingList}
            skeletonRows={pageSize}
            manualPagination={{
              pageIndex: page,
              pageCount,
              onPageChange: setPage,
            }}
          />
        </div>
      </PageContainer>

      <TagDialog
        mode="add"
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(values) => handleCreate(values.name, values.color)}
        loading={isCreating}
      />

      <TagDialog
        mode="edit"
        open={editOpen}
        onClose={() => setEditOpen(false)}
        value={selected}
        onSubmit={(values) =>
          selected ? handleEdit(selected.id, values) : undefined
        }
        loading={isEditing}
        onDelete={
          hasRemovePermission && selected?.active
            ? () => setDeleteOpen(true)
            : undefined
        }
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => { if (!next) setDeleteOpen(false); }}
        title={t("tags.delete.title", { name: selected?.name })}
        confirmLabel={t("common.delete")}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />
    </>
  );
}

interface TagDialogValues {
  name: string;
  color: string | null;
  active?: boolean;
}

interface TagDialogProps {
  mode: "add" | "edit";
  open: boolean;
  onClose: () => void;
  onSubmit: (values: TagDialogValues) => void;
  loading: boolean;
  value?: Tag | null;
  /** Only shown in `mode='edit'`. Triggers the parent's ConfirmDialog. */
  onDelete?: () => void;
}

function TagDialog({
  mode,
  open,
  onClose,
  onSubmit,
  loading,
  value,
  onDelete,
}: TagDialogProps) {
  const { t } = useTranslation();

  const schema = z.object({
    name: z.string().trim().min(1, t("common.required", "Required")),
    color: z
      .string()
      .nullable()
      .refine(
        (v) => v === null || v === "" || isValidHex(v),
        { message: t("tags.errors.invalid-hex", "Use #RRGGBB") },
      ),
    active: z.boolean(),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: value?.name ?? "",
      color: value?.color ?? null,
      active: value?.active ?? true,
    },
  });

  // Re-seed defaults whenever the dialog opens with a different value
  // (RHF doesn't reset between opens otherwise).
  useEffect(() => {
    if (open) {
      form.reset({
        name: value?.name ?? "",
        color: value?.color ?? null,
        active: value?.active ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value?.id]);

  const watchedName = form.watch("name");
  const watchedColor = form.watch("color");

  const handleSave = (values: Values) => {
    if (mode === "add") {
      onSubmit({ name: values.name.trim(), color: values.color ?? null });
    } else {
      onSubmit({
        name: values.name.trim(),
        color: values.color ?? null,
        active: values.active,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? t("tags.add-dialog.title") : t("tags.edit-dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("tags.add-dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tags.fields.name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("tags.fields.name-placeholder")}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tags.fields.color")}</FormLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    {DEFAULT_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => field.onChange(c)}
                        className="size-6 rounded-full border border-border ring-offset-background transition-shadow focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
                        style={{
                          backgroundColor: c,
                          boxShadow:
                            field.value === c ? "0 0 0 2px var(--ring)" : undefined,
                        }}
                        aria-label={c}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => field.onChange(null)}
                      className="rounded-full border border-dashed border-border px-2 py-1 text-xs text-muted-foreground"
                    >
                      {t("tags.fields.no-color")}
                    </button>
                  </div>
                  <FormControl>
                    <Input
                      className="mt-2"
                      placeholder="#RRGGBB"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === "edit" ? (
              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        id="tag-active"
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(Boolean(v))}
                      />
                    </FormControl>
                    <Label htmlFor="tag-active">{t("tags.fields.active")}</Label>
                  </FormItem>
                )}
              />
            ) : null}

            <div className="space-y-1">
              <Label>{t("tags.fields.preview")}</Label>
              <div className="flex">
                <Badge
                  style={{
                    backgroundColor: watchedColor ?? undefined,
                    color: watchedColor
                      ? parseInt(watchedColor.slice(1), 16) > 0x888888
                        ? "#000"
                        : "#fff"
                      : undefined,
                  }}
                >
                  {watchedName || t("tags.fields.preview-placeholder")}
                </Badge>
              </div>
            </div>

            <DialogFooter className="sm:justify-between">
              {mode === "edit" && onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onDelete}
                  disabled={loading}
                  aria-label={t("common.delete", "Delete")}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2Icon className="size-4" />
                  {t("common.delete")}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? t("common.loading", "Loading…") : t("common.save")}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

